import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Clock, 
  User, 
  ArrowLeft,
  Tag,
  Share2,
  Eye,
  BookOpen
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { blogs, blogCategories } from '../data/blogs';
import { getBlogsFromAPI, incrementBlogViews } from '../utils/blogStorageHybrid';
import { getBlogTranslation } from '../i18n/locales/blogTranslations';

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, currentLanguage } = useTranslation();
  const [modalImage, setModalImage] = useState(null);
  
  // Функция для получения правильного пути в зависимости от языка
  const getLocalizedPath = (path) => {
    if (currentLanguage === 'ru') {
      return path === '/' ? '/ru' : `/ru${path}`;
    }
    return path;
  };
  
  // Находим блог по slug из статических данных и API
  const staticBlogs = blogs;
  const [adminBlogs, setAdminBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Загружаем админ блоги при монтировании
  useEffect(() => {
    const loadAdminBlogs = async () => {
      try {
        const blogs = await getBlogsFromAPI();
        setAdminBlogs(blogs);
      } catch (error) {
        console.error('Ошибка загрузки админ блогов:', error);
        setAdminBlogs([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadAdminBlogs();
  }, []);
  
  // Фильтруем админ блоги, исключая статические (id 1-5)
  const filteredAdminBlogs = adminBlogs.filter(blog => !blog.id || blog.id > 5);
  
  const allBlogs = [...staticBlogs, ...filteredAdminBlogs];
  
  const blog = allBlogs.find(b => {
    if (b.slug === slug && b.status === 'published') {
      // Для статических блогов (id <= 5) показываем на обоих языках
      if (b.id && b.id <= 5) {
        return true;
      }
      // Для админ блогов проверяем язык
      return b.language === currentLanguage;
    }
    return false;
  });

  // Функции для поделиться
  const handleShare = async () => {
    const url = window.location.href;
    const title = blog?.title || 'Статья';
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          url: url,
        });
      } catch (error) {
        console.log('Поделиться отменено');
      }
    } else {
      // Fallback для браузеров без поддержки Web Share API
      try {
        await navigator.clipboard.writeText(url);
        alert('Ссылка скопирована в буфер обмена!');
      } catch (error) {
        // Если не удалось скопировать, показываем модальное окно
        const shareText = `Поделиться: ${title}\n${url}`;
        prompt('Скопируйте ссылку:', shareText);
      }
    }
  };

  // Увеличиваем просмотры при загрузке страницы
  useEffect(() => {
    if (blog && blog.id && !loading) {
      incrementBlogViews(blog.id);
    }
  }, [blog, loading]);

  // Обработка кликов по изображениям результатов
  useEffect(() => {
    const handleImageClick = (e) => {
      if (e.target.closest('[data-result-image]')) {
        e.preventDefault();
        const img = e.target.closest('[data-result-image]').querySelector('img');
        if (img) {
          setModalImage({
            src: img.src,
            alt: img.alt
          });
        }
      }
    };

    document.addEventListener('click', handleImageClick);
    return () => document.removeEventListener('click', handleImageClick);
  }, []);

  // Функции для слайдера результатов
  useEffect(() => {
    window.nextSlide = () => {
      const slider = document.getElementById('resultsSlider');
      const indicators = document.querySelectorAll('.slider-indicator');
      if (slider) {
        const currentTransform = slider.style.transform || 'translateX(0%)';
        const currentSlide = currentTransform.includes('-100%') ? 1 : 0;
        const nextSlide = currentSlide === 0 ? 1 : 0;
        
        slider.style.transform = `translateX(-${nextSlide * 100}%)`;
        
        // Обновляем индикаторы
        indicators.forEach((indicator, index) => {
          if (index === nextSlide) {
            indicator.className = 'w-3 h-3 rounded-full bg-blue-600 transition-all slider-indicator';
          } else {
            indicator.className = 'w-3 h-3 rounded-full bg-gray-300 hover:bg-gray-400 transition-all slider-indicator';
          }
        });
      }
    };

    window.previousSlide = () => {
      const slider = document.getElementById('resultsSlider');
      const indicators = document.querySelectorAll('.slider-indicator');
      if (slider) {
        const currentTransform = slider.style.transform || 'translateX(0%)';
        const currentSlide = currentTransform.includes('-100%') ? 1 : 0;
        const prevSlide = currentSlide === 0 ? 1 : 0;
        
        slider.style.transform = `translateX(-${prevSlide * 100}%)`;
        
        // Обновляем индикаторы
        indicators.forEach((indicator, index) => {
          if (index === prevSlide) {
            indicator.className = 'w-3 h-3 rounded-full bg-blue-600 transition-all slider-indicator';
          } else {
            indicator.className = 'w-3 h-3 rounded-full bg-gray-300 hover:bg-gray-400 transition-all slider-indicator';
          }
        });
      }
    };

    window.goToSlide = (slideIndex) => {
      const slider = document.getElementById('resultsSlider');
      const indicators = document.querySelectorAll('.slider-indicator');
      if (slider) {
        slider.style.transform = `translateX(-${slideIndex * 100}%)`;
        
        // Обновляем индикаторы
        indicators.forEach((indicator, index) => {
          if (index === slideIndex) {
            indicator.className = 'w-3 h-3 rounded-full bg-blue-600 transition-all slider-indicator';
          } else {
            indicator.className = 'w-3 h-3 rounded-full bg-gray-300 hover:bg-gray-400 transition-all slider-indicator';
          }
        });
      }
    };

    return () => {
      delete window.nextSlide;
      delete window.previousSlide;
      delete window.goToSlide;
    };
  }, []);
  
  if (!blog) {
    return (
      <div className="min-h-screen bg-white text-dark-900">
        <Header />
        <main className="container-spacious py-16 sm:py-20">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Статья не найдена</h1>
            <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">Запрашиваемая статья не существует или была удалена.</p>
            <button
              onClick={() => navigate(getLocalizedPath('/blog'))}
              className="btn-primary inline-flex items-center gap-2 text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3"
            >
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                {t('common.back')}
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(currentLanguage === 'de' ? 'de-DE' : 'ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryName = (categoryId) => {
    const category = blogCategories.find(cat => cat.id === categoryId);
    return category ? t(`blog.categories.${categoryId}`) : t('blog.categories.no_category');
  };

  const getCategoryColor = (categoryId) => {
    const category = blogCategories.find(cat => cat.id === categoryId);
    return category ? category.color : 'bg-gray-100 text-gray-800';
  };

  const renderTranslatedContent = (postId) => {
    const content = getBlogTranslation(postId, 'content', currentLanguage);
    if (content && typeof content === 'object') {
      // Для статьи 1 (гипнотерапия для детей)
      if (postId === 1) {
        return `
          <div class="space-y-8">
            <h2 class="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">${content.main_title}</h2>
            <p class="text-base sm:text-lg leading-relaxed mb-6 sm:mb-8">${content.intro}</p>
            
            <div class="space-y-4 sm:space-y-6">
              <h3 class="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">${content.basics_title}</h3>
              <p class="text-sm sm:text-base lg:text-lg leading-relaxed">${content.basics_text}</p>
            </div>
            
            <h3 class="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">${content.process_title}</h3>
            
            <div class="space-y-4 sm:space-y-6">
              <div class="space-y-3 sm:space-y-4">
                <h4 class="text-base sm:text-lg font-semibold mb-2 sm:mb-3">${content.step1_title}</h4>
                <p class="text-sm sm:text-base lg:text-lg leading-relaxed">${content.step1_text}</p>
              </div>
              
              <div class="space-y-3 sm:space-y-4">
                <h4 class="text-base sm:text-lg font-semibold mb-2 sm:mb-3">${content.step2_title}</h4>
                <p class="text-sm sm:text-base lg:text-lg leading-relaxed">${content.step2_text}</p>
              </div>
              
              <div class="space-y-3 sm:space-y-4">
                <h4 class="text-base sm:text-lg font-semibold mb-2 sm:mb-3">${content.step3_title}</h4>
                <p class="text-sm sm:text-base lg:text-lg leading-relaxed">${content.step3_text}</p>
              </div>
              
              <div class="space-y-3 sm:space-y-4">
                <h4 class="text-base sm:text-lg font-semibold mb-2 sm:mb-3">${content.step4_title}</h4>
                <ul class="space-y-2 sm:space-y-3 text-sm sm:text-base lg:text-lg leading-relaxed">
                  <li><strong>${content.step4_regression.split(':')[0]}:</strong> ${content.step4_regression.split(':')[1]}</li>
                  <li><strong>${content.step4_suggestions.split(':')[0]}:</strong> ${content.step4_suggestions.split(':')[1]}</li>
                  <li><strong>${content.step4_resources.split(':')[0]}:</strong> ${content.step4_resources.split(':')[1]}</li>
                  <li><strong>${content.step4_anchoring.split(':')[0]}:</strong> ${content.step4_anchoring.split(':')[1]}</li>
                </ul>
              </div>
              
              <div class="space-y-3 sm:space-y-4">
                <h4 class="text-base sm:text-lg font-semibold mb-2 sm:mb-3">${content.step5_title}</h4>
                <p class="text-sm sm:text-base lg:text-lg leading-relaxed">${content.step5_text}</p>
              </div>
            </div>
            
            <div class="space-y-6">
              <h3 class="text-xl font-semibold mb-4">${content.journeys_title}</h3>
              <p class="text-lg leading-relaxed mb-4">${content.journeys_text}</p>
              
              <div class="bg-blue-50 border-l-4 border-blue-400 p-6 my-6 rounded-r-lg">
                <p class="text-blue-800 text-lg"><strong>${content.journey_example}</strong></p>
              </div>
            </div>
            
            <div class="space-y-6">
              <h3 class="text-xl font-semibold mb-4">${content.parents_title}</h3>
              <p class="text-lg leading-relaxed mb-4">${content.parents_text}</p>
              <ul class="space-y-3 text-lg leading-relaxed">
                <li><strong>${content.parents_reinforcement.split(':')[0]}:</strong> ${content.parents_reinforcement.split(':')[1]}</li>
                <li><strong>${content.parents_communication.split(':')[0]}:</strong> ${content.parents_communication.split(':')[1]}</li>
                <li><strong>${content.parents_example.split(':')[0]}:</strong> ${content.parents_example.split(':')[1]}</li>
              </ul>
            </div>
            
            <div class="space-y-6">
              <h3 class="text-xl font-semibold mb-4">${content.additional_title}</h3>
              <ul class="space-y-3 text-lg leading-relaxed">
                <li><strong>${content.additional_combination.split(':')[0]}:</strong> ${content.additional_combination.split(':')[1]}</li>
                <li><strong>${content.additional_homeopathy.split(':')[0]}:</strong> ${content.additional_homeopathy.split(':')[1]}</li>
                <li><strong>${content.additional_regularity.split(':')[0]}:</strong> ${content.additional_regularity.split(':')[1]}</li>
              </ul>
            </div>
          </div>
        `;
      }
      
      // Для статьи 2 (психосоматика и кожа)
      if (postId === 2) {
        return `
          <div class="space-y-8">
            <h2 class="text-3xl font-bold mb-6">${content.main_title}</h2>
            
            <div class="space-y-6">
              <h3 class="text-xl font-semibold mb-4">${content.stress_title}</h3>
              <p class="text-lg leading-relaxed">${content.stress_text}</p>
            </div>
            
            <div class="space-y-6">
              <h3 class="text-xl font-semibold mb-4">${content.emotions_title}</h3>
              <p class="text-lg leading-relaxed">${content.emotions_text}</p>
            </div>
            
            <div class="space-y-6">
              <h3 class="text-xl font-semibold mb-4">${content.conflicts_title}</h3>
              <p class="text-lg leading-relaxed">${content.conflicts_text}</p>
            </div>
            
            <h2 class="text-3xl font-bold mb-6">${content.hypnosis_title}</h2>
            <p class="text-lg leading-relaxed mb-8">${content.hypnosis_text}</p>
            
            <div class="space-y-6">
              <h3 class="text-xl font-semibold mb-4">${content.achievements_title}</h3>
              <ul class="space-y-4 text-lg leading-relaxed">
                ${content.achievements_items.map(item => `<li><strong>${item.split(':')[0]}:</strong> ${item.split(':')[1]}</li>`).join('')}
              </ul>
            </div>
            
            <div class="space-y-6">
              <h3 class="text-xl font-semibold mb-4">${content.how_works_title}</h3>
              <p class="text-lg leading-relaxed">${content.how_works_text}</p>
            </div>
            
            <h2 class="text-3xl font-bold mb-6">${content.success_title}</h2>
            <p class="text-lg leading-relaxed mb-8">${content.success_text}</p>
            
            <div class="bg-blue-50 border-l-4 border-blue-400 p-6 my-8 rounded-r-lg">
              <p class="text-blue-800 font-medium text-lg">${content.important_note}</p>
            </div>
            
            <h2 class="text-3xl font-bold mb-6">${content.program_title}</h2>
            
            <div class="space-y-8">
              <div class="space-y-4">
                <h3 class="text-xl font-semibold mb-4">${content.step1_title}</h3>
                <p class="text-lg leading-relaxed"><strong>${content.step1_text.split(':')[0]}:</strong> ${content.step1_text.split(':')[1]}</p>
              </div>
              
              <div class="space-y-4">
                <h3 class="text-xl font-semibold mb-4">${content.step2_title}</h3>
                <ul class="space-y-3 text-lg leading-relaxed">
                  ${content.step2_items.map(item => `<li><strong>${item.split(':')[0]}:</strong> ${item.split(':')[1]}</li>`).join('')}
                </ul>
              </div>
              
              <div class="space-y-4">
                <h3 class="text-xl font-semibold mb-4">${content.step3_title}</h3>
                <p class="text-lg leading-relaxed"><strong>${content.step3_text.split(':')[0]}:</strong> ${content.step3_text.split(':')[1]}</p>
              </div>
              
              <div class="space-y-4">
                <h3 class="text-xl font-semibold mb-4">${content.step4_title}</h3>
                <ul class="space-y-3 text-lg leading-relaxed">
                  ${content.step4_items.map(item => `<li><strong>${item.split(':')[0]}:</strong> ${item.split(':')[1]}</li>`).join('')}
                </ul>
              </div>
            </div>
            
            <h2 class="text-3xl font-bold mb-6">${content.aspects_title}</h2>
            <ul class="space-y-4 text-lg leading-relaxed">
              ${content.aspects_items.map(item => `<li><strong>${item.split(':')[0]}:</strong> ${item.split(':')[1]}</li>`).join('')}
            </ul>
            
            <div class="bg-yellow-50 border-l-4 border-yellow-400 p-6 my-8 rounded-r-lg">
              <p class="text-yellow-800 text-lg"><strong>${content.disclaimer}</strong></p>
            </div>
            
            <h2 class="text-3xl font-bold mb-6">${content.conclusion_title}</h2>
            <p class="text-lg leading-relaxed mb-6">${content.conclusion_text}</p>
            
            <p class="text-lg leading-relaxed">${content.cta_text}</p>
            
            <div class="mt-12">
              <h2 class="text-3xl font-bold mb-6 text-center">${content.results_title}</h2>
              <p class="text-lg leading-relaxed mb-8 text-center text-gray-600">
                ${content.results_text}
              </p>
            </div>
          </div>
        `;
      }
      
      // Для статьи 3 (история г-жи Мюллер)
      if (postId === 3) {
        return `
          <div class="space-y-8">
            <div class="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg">
              <p class="text-blue-800 text-lg font-medium">
                <strong>${content.practical_example}</strong><br>
                <span class="text-sm text-blue-600">${content.updated}</span>
              </p>
            </div>
            
            <h2 class="text-3xl font-bold mb-6">${content.main_title}</h2>
            
            <div class="space-y-6">
              <p class="text-lg leading-relaxed">
                ${content.intro}
              </p>
            </div>
            
            <h3 class="text-xl font-semibold mb-4">${content.beginning_title}</h3>
            <p class="text-lg leading-relaxed mb-6">
              ${content.beginning_text}
            </p>
            
            <h3 class="text-xl font-semibold mb-4">${content.transformation_title}</h3>
            <div class="space-y-4">
              <p class="text-lg leading-relaxed">
                ${content.transformation_text}
              </p>
              
              <div class="bg-green-50 border-l-4 border-green-400 p-6 rounded-r-lg">
                <h4 class="text-lg font-semibold text-green-800 mb-3">${content.achievements_title}</h4>
                <ul class="space-y-2 text-green-700">
                  ${content.achievements_items.map(item => `<li>• ${item}</li>`).join('')}
                </ul>
              </div>
            </div>
            
            <h3 class="text-xl font-semibold mb-4">${content.results_title}</h3>
            <div class="space-y-4">
              <p class="text-lg leading-relaxed">
                ${content.results_text}
              </p>
              
              <div class="bg-rose-50 border-l-4 border-rose-400 p-6 rounded-r-lg">
                <p class="text-rose-800 text-lg font-medium">
                  <strong>${content.today_text}</strong>
                </p>
              </div>
            </div>
            
            <h3 class="text-xl font-semibold mb-4">${content.lessons_title}</h3>
            <div class="space-y-4">
              <ul class="space-y-3 text-lg leading-relaxed">
                ${content.lessons_items.map(item => `<li><strong>${item.split(':')[0]}:</strong> ${item.split(':')[1]}</li>`).join('')}
              </ul>
            </div>
            
            <div class="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg">
              <p class="text-yellow-800 text-lg">
                <strong>${content.important_note}</strong>
              </p>
            </div>
            
            <h3 class="text-xl font-semibold mb-4">${content.ready_title}</h3>
            <p class="text-lg leading-relaxed mb-6">
              ${content.ready_text}
            </p>
          </div>
        `;
      }
      
      // Для статьи 4 (панические атаки)
      if (postId === 4) {
        return `
          <div class="space-y-8">
            <h2 class="text-3xl font-bold mb-6">${content.main_title}</h2>
            
            <div class="space-y-6">
              <p class="text-lg leading-relaxed">
                ${content.intro}
              </p>
            </div>
            
            <h3 class="text-xl font-semibold mb-4">${content.features_title}</h3>
            <div class="space-y-4">
              <p class="text-lg leading-relaxed">
                ${content.features_text}
              </p>
              
              <div class="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg">
                <h4 class="text-lg font-semibold text-red-800 mb-3">${content.signs_title}</h4>
                <ul class="space-y-2 text-red-700">
                  ${content.signs_items.map(item => `<li>• ${item}</li>`).join('')}
                </ul>
              </div>
            </div>
            
            <h3 class="text-xl font-semibold mb-4">${content.therapy_title}</h3>
            <div class="space-y-4">
              <p class="text-lg leading-relaxed">
                ${content.therapy_text}
              </p>
              
              <div class="grid md:grid-cols-2 gap-6">
                <div class="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <h4 class="text-lg font-semibold text-blue-800 mb-3">${content.triggers_title}</h4>
                  <p class="text-blue-700">
                    ${content.triggers_text}
                  </p>
                </div>
                
                <div class="bg-green-50 border border-green-200 rounded-xl p-6">
                  <h4 class="text-lg font-semibold text-green-800 mb-3">${content.restructuring_title}</h4>
                  <p class="text-green-700">
                    ${content.restructuring_text}
                  </p>
                </div>
              </div>
            </div>
          </div>
        `;
      }
      
      // Для статьи 5 (гипноз)
      if (postId === 5) {
        return `
          <div class="space-y-8">
            <div class="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg">
              <p class="text-blue-800 text-lg font-medium">
                <strong>${content.updated}</strong>
              </p>
            </div>
            
            <h2 class="text-3xl font-bold mb-6">${content.main_title}</h2>
            
            <div class="space-y-6">
              <p class="text-lg leading-relaxed">
                ${content.intro}
              </p>
            </div>
            
            <h3 class="text-xl font-semibold mb-4">${content.how_helps_title}</h3>
            <div class="space-y-4">
              <p class="text-lg leading-relaxed">
                ${content.how_helps_text}
              </p>
              
              <div class="bg-green-50 border-l-4 border-green-400 p-6 rounded-r-lg">
                <h4 class="text-lg font-semibold text-green-800 mb-3">${content.what_happens_title}</h4>
                <ul class="space-y-2 text-green-700">
                  ${content.what_happens_items.map(item => `<li>• ${item}</li>`).join('')}
                </ul>
              </div>
            </div>
            
            <h3 class="text-xl font-semibold mb-4">${content.results_title}</h3>
            <div class="space-y-4">
              <p class="text-lg leading-relaxed">
                ${content.results_text}
              </p>
              
              <div class="grid md:grid-cols-2 gap-6">
                <div class="bg-purple-50 border border-purple-200 rounded-xl p-6">
                  <h4 class="text-lg font-semibold text-purple-800 mb-3">${content.new_opportunities_title}</h4>
                  <ul class="space-y-2 text-purple-700">
                    ${content.new_opportunities_items.map(item => `<li>• ${item}</li>`).join('')}
                  </ul>
                </div>
                
                <div class="bg-orange-50 border border-orange-200 rounded-xl p-6">
                  <h4 class="text-lg font-semibold text-orange-800 mb-3">${content.personal_growth_title}</h4>
                  <ul class="space-y-2 text-orange-700">
                    ${content.personal_growth_items.map(item => `<li>• ${item}</li>`).join('')}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        `;
      }
    }
    return blog.content;
  };

  return (
    <div className="min-h-screen bg-white text-dark-900">
      <Header />
      <main>
        {/* Хлебные крошки */}
        <section className="py-6 sm:py-8 bg-gray-50">
          <div className="container-spacious">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 text-xs sm:text-sm text-gray-600"
            >
              <button
                onClick={() => navigate(getLocalizedPath('/blog'))}
                className="hover:text-rose-600 transition-colors"
              >
                Блог
              </button>
              <span>/</span>
              <span className="text-gray-900 font-medium truncate">{getBlogTranslation(blog.id, 'title', currentLanguage) || blog.title}</span>
            </motion.div>
          </div>
        </section>

        {/* Заголовок статьи */}
        <section className="py-12 sm:py-16 bg-white">
          <div className="container-spacious">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="max-w-4xl mx-auto text-center"
            >
              {/* Категория */}
              <div className="flex flex-wrap items-center justify-center gap-2 mb-4 sm:mb-6">
                <Tag className="w-4 h-4 sm:w-5 sm:h-5 text-rose-600" />
                <span className={`text-xs sm:text-sm font-medium px-2 sm:px-3 py-1 rounded-full ${getCategoryColor(blog.category)}`}>
                  {getCategoryName(blog.category)}
                </span>
                {blog.featured && (
                  <div className="flex items-center gap-1">
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-medium">
                      {t('blog.article.featured')}
                    </span>
                  </div>
                )}
              </div>

              {/* Заголовок */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-dark-900 mb-4 sm:mb-6 leading-tight">
                {getBlogTranslation(blog.id, 'title', currentLanguage) || blog.title}
              </h1>

              {/* Описание */}
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed max-w-3xl mx-auto">
                {getBlogTranslation(blog.id, 'excerpt', currentLanguage) || blog.excerpt}
              </p>

              {/* Метаинформация */}
              <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-500 mb-6 sm:mb-8">
                <div className="flex items-center gap-1 sm:gap-2">
                  <User className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>{t('blog.article.author')}</span>
                </div>
                <div className="flex items-center gap-1 sm:gap-2">
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>{formatDate(blog.publishedAt)}</span>
                </div>
                <div className="flex items-center gap-1 sm:gap-2">
                  <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>{blog.readingTime} {t('blog.article.minutes_reading')}</span>
                </div>
                <div className="flex items-center gap-1 sm:gap-2">
                  <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>{blog.views || 0} {t('blog.article.views')}</span>
                </div>
              </div>

              {/* Кнопка назад */}
              <div className="flex justify-center">
                <button
                  onClick={() => navigate(getLocalizedPath('/blog'))}
                  className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors text-sm sm:text-base"
                >
                  <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                  {t('common.back')}
                </button>
              </div>

              {/* Кнопки действий */}
              <div className="flex justify-center gap-4 mt-6">
                <button
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-xl font-medium transition-colors text-sm sm:text-base"
                >
                  <Share2 className="w-4 h-4" />
                  <span>{t('blog.article.share')}</span>
                </button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Изображение статьи */}
        {blog.featuredImage && (
          <section className="py-6 sm:py-8 bg-gray-50">
            <div className="container-spacious">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                className="max-w-4xl mx-auto"
              >
                <div className="relative h-64 sm:h-80 lg:h-96 bg-gradient-to-br from-rose-100 to-purple-100 rounded-2xl overflow-hidden">
                  <img
                    src={blog.featuredImage}
                    alt={getBlogTranslation(blog.id, 'title', currentLanguage) || blog.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  {!blog.featuredImage && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <BookOpen className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 text-rose-600" />
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </section>
        )}

        {/* Содержимое статьи */}
        <section className="py-12 sm:py-16 bg-white">
          <div className="container-spacious">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="max-w-4xl mx-auto"
            >
              <div className="prose prose-sm sm:prose-base lg:prose-lg prose-rose max-w-none">
                <div dangerouslySetInnerHTML={{ __html: renderTranslatedContent(blog.id) }} />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Теги */}
        <section className="py-8 sm:py-12 bg-gray-50">
          <div className="container-spacious">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="max-w-4xl mx-auto"
            >
              <h3 className="text-base sm:text-lg font-semibold text-dark-900 mb-3 sm:mb-4">{t('blog.article.tags_label')}</h3>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {blog.tags && (Array.isArray(blog.tags) ? blog.tags : blog.tags.split(',').map(tag => tag.trim())).map((tag) => (
                  <span
                    key={tag}
                    className="text-xs sm:text-sm bg-white text-gray-700 px-2 sm:px-3 py-1 rounded-full border border-gray-200 hover:bg-rose-50 hover:border-rose-200 transition-colors cursor-pointer"
                  >
                    #{getBlogTranslation(tag, 'tag', currentLanguage)}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Действия */}
        <section className="py-8 sm:py-12 bg-white">
          <div className="container-spacious">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="max-w-4xl mx-auto"
            >
              <div className="flex justify-center p-6 sm:p-8 bg-gray-50 rounded-2xl">
                <button 
                  onClick={handleShare}
                  className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-white hover:bg-gray-100 text-gray-700 rounded-xl font-medium transition-colors border border-gray-200 text-xs sm:text-sm"
                >
                  <Share2 className="w-3 h-3 sm:w-4 sm:h-4" />
                  {t('blog.article.share')}
                </button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="container-spacious">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
              className="text-center"
            >
              <div className="block-primary text-center max-w-2xl mx-auto">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
                  {t('blog.cta.title')}
                </h3>
                <p className="text-sm sm:text-base mb-4 sm:mb-6 text-gray-600">
                  {t('blog.cta.description')}
                </p>
                <a
                  href={getLocalizedPath("/contact")}
                  className="btn-primary inline-flex items-center gap-2 text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3"
                >
                  {t('blog.cta.button')}
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Модальное окно для просмотра изображений */}
      {modalImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-2 sm:p-4" onClick={() => setModalImage(null)}>
          <div className="relative max-w-4xl max-h-[95vh] sm:max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => setModalImage(null)} 
              className="absolute -top-8 sm:-top-12 right-0 text-white text-lg sm:text-2xl hover:text-gray-300 transition-colors z-10"
            >
              ✕ Закрыть
            </button>
            <img 
              src={modalImage.src} 
              alt={modalImage.alt} 
              className="max-w-full max-h-[85vh] sm:max-h-[90vh] object-contain rounded-lg shadow-2xl"
            />
            <p className="text-white text-center mt-2 sm:mt-4 text-sm sm:text-lg">{modalImage.alt}</p>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

